import { useState } from "react"
import { useAsync, useAsyncCallback } from "react-async-hook"
import { useLocation, useNavigate } from "react-router-dom"
import { useApp } from "../../AppProvider"

import { toTitleCase } from "@/utils/str"
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { PAGE_SIZE } from "../../constants"
import { useApi } from "../../hooks/useApi"
import { UserWithOrgs } from "../../types/User"
import { classNames } from "../../utils/classNames"
import { ConfirmModal } from "../ConfirmModal"
import { Button, Spinner } from "../common/"

type Props = {
  showOrganization?: boolean
  orgId?: string
}

const columnHelper = createColumnHelper<UserWithOrgs>()
const columns = [
  columnHelper.accessor("email", {
    header: "Email",
  }),
  columnHelper.accessor("name", {
    header: "Name",
  }),
  columnHelper.accessor((row) => row.organizations?.[0], {
    id: "organization",
    header: "Organization",
    cell: (info) => {
      return <span>{info?.getValue()?.name ?? "-"}</span>
    },
  }),
  columnHelper.accessor((row) => row.organizations?.[0], {
    id: "role",
    header: "Role",
    cell: (info) => {
      return <span>{toTitleCase(info?.getValue()?.role) ?? "-"}</span>
    },
  }),
  columnHelper.display({
    id: "actions",
    cell: (props: any) => {
      return (
        <div className="flex items-center justify-end gap-x-2">
          <Button
            variant="outline"
            onClick={() => props.navigate(`${props.pathname}/${props.row.original._id}`)}
          >
            Edit
          </Button>
          <Button variant="danger" onClick={() => props.setUserToDelete(props.row.original)}>
            Delete
          </Button>
        </div>
      )
    },
  }),
]

export const UserList = ({ showOrganization, orgId }: Props) => {
  const { getUsers, getUsersForOrg, deleteUser, deleteUserForOrg } = useApi()
  const { addToast } = useApp()
  const navigate = useNavigate()
  const [currentPage] = useState(1)
  const [userToDelete, setUserToDelete] = useState<any>(undefined)
  const { pathname } = useLocation()

  const usersAsync = useAsync(async () => {
    if (!orgId) {
      return getUsers((currentPage - 1) * PAGE_SIZE, PAGE_SIZE)
    } else {
      return getUsersForOrg(orgId, (currentPage - 1) * PAGE_SIZE, PAGE_SIZE)
    }
  }, [currentPage])
  const users = usersAsync.result?.users || []

  const table = useReactTable({
    data: users,
    columns,
    state: {
      columnVisibility: {
        organization: Boolean(showOrganization),
      },
    },
    getCoreRowModel: getCoreRowModel(),
  })

  const deleteUserAsync = useAsyncCallback(async () => {
    try {
      const result = !!orgId
        ? await deleteUserForOrg(orgId, userToDelete._id)
        : await deleteUser(userToDelete._id)
      addToast("Success", `User ${userToDelete.email} deleted`, "success")
      setUserToDelete(undefined)
      usersAsync.execute()
      return result
    } catch (error: any) {
      addToast("Error", error.message || `Error deleting user ${userToDelete.email}`, "error")
      setUserToDelete(undefined)
    }
  })

  return (
    <div className="card overflow-hidden">
      <div className="flex items-start justify-between">
        <div className="text-lg font-semibold text-gray-900">Users</div>
        <Button onClick={() => navigate(`${pathname}/new`)}>Add</Button>
      </div>

      <ConfirmModal
        open={!!userToDelete}
        setOpen={() => setUserToDelete(undefined)}
        onCancel={() => setUserToDelete(undefined)}
        onConfirm={deleteUserAsync.execute}
        confirmVariant="danger"
        header="Delete user"
        body={
          <>
            Are you sure you want to delete <strong>{userToDelete?.email}</strong>?
          </>
        }
      />

      {usersAsync.loading ? (
        <div className="mx-auto my-3">
          <Spinner />
        </div>
      ) : !users || users.length <= 0 ? (
        <div>No users found</div>
      ) : (
        <div className="flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, i) => (
                        <th
                          scope="col"
                          className={classNames(
                            "relative py-3.5 text-left text-sm font-semibold text-gray-900",
                            i === 0
                              ? "pr-3 pl-4 sm:pl-0"
                              : i === headerGroup.headers.length - 1
                                ? "pr-4 pl-3 sm:pr-0"
                                : "px-3",
                          )}
                          key={header.id}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell, i) => (
                        <td
                          className={classNames(
                            "py-4 text-sm font-medium whitespace-nowrap text-gray-900",
                            i === 0
                              ? "pr-3 pl-4 sm:pl-0"
                              : i === row.getVisibleCells().length - 1
                                ? "pr-4 pl-3 sm:pr-0"
                                : "px-3",
                          )}
                          key={cell.id}
                        >
                          {flexRender(cell.column.columnDef.cell, {
                            ...cell.getContext(),
                            navigate,
                            pathname,
                            setUserToDelete,
                          })}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
